import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useShortcuts from '../../hooks/useShortcuts'
import { RootState } from '../../store'
import { useDispatch, useSelector } from 'react-redux'
import { WidgetListItems } from '../../util/sidebar'
import commonActions from '../../actions/common.actions'
import { GlobalShortcutsKeyName } from './ShortcutsKeymap'
import { ShortcutsCategories, ShortcutType } from './Shortcuts.types'
import { setQuickOrderData } from '../../reducer/orders.reducer'
import { Broker_OrderType, OrderType, Product, TransactionType } from '../../proto/generated/order'
import { selectPrimarySecurity } from '../../selectors/home'
import { SecurityCategory_CategoryCode } from '../../proto/generated/security'
import { selectPrimaryTradingAccount, selectTradingAccounts } from '../../selectors/user'
import { TradingAccountStatus } from '../../proto/generated/trading-bridge'
import { getSecurityAsync } from '../../util/security'
import { dispatchOpenQuickTrade } from '../../util/openQuickTrade'
import AnalyticsManager from '../../analytics/AnalyticsManager'

const GlobalShortcutsRegister = () => {
    const dispatch = useDispatch()
    const rightSideWidget = useSelector((state: RootState) => state.commonStore.rightSideWidget)
    const primarySecurity = useSelector(selectPrimarySecurity)
    const primarySecurityRef = useRef(primarySecurity)
    const { key: primaryKey } = primarySecurity
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (primaryKey) getSecurityAsync(primaryKey).then(setSecurity); else setSecurity(undefined) }, [primaryKey])
    const tradingAccounts = useSelector(selectTradingAccounts)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)

    useEffect(() => {
        primarySecurityRef.current = primarySecurity
    }, [primarySecurity])

    const ToggleTradePanel = useCallback(() => {
        AnalyticsManager.logEvent('Trade Panel Toggled', { source: 'keyboard', opened:  rightSideWidget === WidgetListItems.TRADE ? 'false' : 'true'})
        commonActions.dispatchRightSideWidget(dispatch, rightSideWidget === WidgetListItems.TRADE ? null : WidgetListItems.TRADE)
    }, [dispatch, rightSideWidget])

    const handlePlaceOrder = useCallback((transactionType: TransactionType, orderType: OrderType, product: Product, brokerOrderType: Broker_OrderType) => {
        const { expressionMode } = primarySecurityRef.current
        if (!security) return
        if (expressionMode || security.categoryCode === SecurityCategory_CategoryCode.INDEX) {
            dispatch(setQuickOrderData({
                openDialog: true,
                mode: 'regular',
                type: 'notTradable',
                isSquareOffOrder: false,
                order: {
                    transactionType,
                    security,
                    editOrder: undefined,
                    brokerOrderType,
                    orderType,
                    product,
                    price: 0,
                    quantity: 0,
                    maxQuantity: 0,
                }
            }))
        } else if (!primaryTradingAccount || !tradingAccounts || tradingAccounts?.length === 0) {
            return
        } else if (primaryTradingAccount.status !== TradingAccountStatus.ACTIVE) {
            return
        } else {
            dispatchOpenQuickTrade({
                dispatch,
                security,
                transactionType,
                brokerOrderType,
                orderType,
                product,
            })
        }
    }, [dispatch, security, primaryTradingAccount, tradingAccounts])

    const placeMarketOrderBuy = useCallback(() => {
        AnalyticsManager.logEvent('Order Dialog Opened ', { source: 'keyboard', type: 'Buy', orderType: 'Market Order'})
        handlePlaceOrder(TransactionType.BUY, OrderType.NORMAL, Product.CNC, Broker_OrderType.MARKET_ORDER)
    }, [handlePlaceOrder])

    const placeMarketOrderSell = useCallback(() => {
        AnalyticsManager.logEvent('Order Dialog Opened ', { source: 'keyboard', type: 'Sell', orderType: 'Market Order'})
        handlePlaceOrder(TransactionType.SELL, OrderType.NORMAL, Product.CNC, Broker_OrderType.MARKET_ORDER)
    }, [handlePlaceOrder])

    const placeLimitOrderBuy = useCallback(() => {
        AnalyticsManager.logEvent('Order Dialog Opened ', { source: 'keyboard', type: 'Buy', orderType: 'Limit Order'})
        handlePlaceOrder(TransactionType.BUY, OrderType.NORMAL, Product.CNC, Broker_OrderType.MARKET_ORDER)
    }, [handlePlaceOrder])

    const placeLimitOrderSell = useCallback(() => {
        AnalyticsManager.logEvent('Order Dialog Opened ', { source: 'keyboard', type: 'Sell', orderType: 'Limit Order'})
        handlePlaceOrder(TransactionType.SELL, OrderType.NORMAL, Product.CNC, Broker_OrderType.MARKET_ORDER)
    }, [handlePlaceOrder])

    const shortcuts: ShortcutType = useMemo(() => ({
        [GlobalShortcutsKeyName.openCloseTradePanel]: ToggleTradePanel,
        [GlobalShortcutsKeyName.placeMarketOrderBuy]: placeMarketOrderBuy,
        [GlobalShortcutsKeyName.placeMarketOrderSell]: placeMarketOrderSell,
        [GlobalShortcutsKeyName.placeLimitOrderBuy]: placeLimitOrderBuy,
        [GlobalShortcutsKeyName.placeLimitOrderSell]: placeLimitOrderSell
    }), [ToggleTradePanel, placeLimitOrderBuy, placeLimitOrderSell, placeMarketOrderBuy, placeMarketOrderSell])

    useShortcuts(ShortcutsCategories.GLOBAL, shortcuts)
}

export default GlobalShortcutsRegister
